import { useCallback, useState } from "react";
import { useVisibilityAwareInterval } from "./useVisibilityAwareInterval";

type Fetcher<T> = (opts: { background?: boolean }) => Promise<T>;

interface UsePolledCounterReturn<T> {
  data: T;
  refresh: () => Promise<void>;
}

/**
 * Periodically fetch a value while the tab is visible.
 *
 * - Initial mount and tab-return-to-visible fire `fetcher({})` (foreground —
 *   counts as user activity for HH-200 sliding idle window).
 * - Every 30s interval tick fires `fetcher({ background: true })` so the
 *   poll sets `X-Activity: background` and does NOT extend the sliding
 *   window.
 *
 * Replaces the per-portal hook duplication that used to live in
 * `apps/{admin,doctor}-portal/src/hooks/useUnreadCount.ts` and
 * `useBadgeCounts.ts`.
 */
export function usePolledCounter<T>(
  fetcher: Fetcher<T>,
  initialState: T,
  intervalMs: number,
): UsePolledCounterReturn<T> {
  const [data, setData] = useState<T>(initialState);

  // Foreground refresh — used on mount, on tab-return-to-visible, and
  // re-exported for user-triggered callers (e.g. route-change refresh
  // in DashboardLayout).
  const refresh = useCallback(async () => {
    try {
      setData(await fetcher({}));
    } catch {
      // best-effort; consumers handle their own UX
    }
  }, [fetcher]);

  // Background poll — drives the recurring schedule.
  const pollRefresh = useCallback(async () => {
    try {
      setData(await fetcher({ background: true }));
    } catch {
      // ignore
    }
  }, [fetcher]);

  useVisibilityAwareInterval(pollRefresh, intervalMs, { initialTick: refresh });

  return { data, refresh };
}
